import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import moment from 'moment'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import attendanceReportApi from '../../../middleware/api/TimeAndAttendance/AttendanceReport/attendanceReportApi'
import { RootState } from '../../../stateStore'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  EmployeeAttendanceReportApiProps,
  EmployeeAttendanceReportResponse,
  EmployeeAttendanceReportSliceState,
  EmployeeDetailsWithAttendanceReport,
} from '../../../types/TimeAndAttendance/AttendanceReport/attendanceReportTypes'
import { currentMonthDateNew, previousMonthNew } from '../../../utils/helper'

// fetch employee attendance report action creator
const getEmployeeAttendanceReport = createAsyncThunk(
  'attendanceReport/getEmployeeAttendanceReport',
  async (props: EmployeeAttendanceReportApiProps, thunkApi) => {
    try {
      return await attendanceReportApi.getEmployeeAttendanceReport(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const searchAttendance = createAsyncThunk(
  'attendanceReport/searchAttendance',
  async (props: EmployeeAttendanceReportApiProps, thunkApi) => {
    try {
      return await attendanceReportApi.searchAttendance(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const storedCountry = localStorage.getItem('employeeCountry')

const monthSelection = (() => {
  const currentMonthValue = currentMonthDateNew(storedCountry ?? '')
  if (currentMonthValue) {
    return Number(currentMonthValue.split('/')[0])
  } else {
    const previousMonthValue = previousMonthNew(storedCountry ?? '')
    return Number(previousMonthValue.split('/')[0])
  }
})()

const initialEmployeeAttendanceReportState: EmployeeAttendanceReportSliceState =
  {
    selectedDate: currentMonthDateNew(),
    size: 0,
    days: [],
    employeeAttendanceReport: [],
    employeeAttendanceReportResponse: {} as EmployeeAttendanceReportResponse,
    isLoading: ApiLoadingState.idle,
    monthDisplay: moment(new Date()).format('MMMM-YYYY'),
    isView: false,
    date: null,
    employeeStatus: 'Active',
    isOther: '',
    count: 0,
    biometric: '',
    searchValue: '',
    employeeShift: '',
    monthSelection,
    paginationCache: {
      currentPage: 0,
      pageSize: 20,
    },
  }

const attendanceReportSlice = createSlice({
  name: 'attendanceReport',
  initialState: initialEmployeeAttendanceReportState,
  reducers: {
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload
    },
    setMonthDisplay: (state, action) => {
      state.monthDisplay = action.payload
    },
    setIsView: (state, action) => {
      state.isView = action.payload
    },
    setEmployeeStatus: (state, action) => {
      state.employeeStatus = action.payload
    },
    setDate: (state, action) => {
      state.date = action.payload
    },
    setIsOther: (state, action) => {
      state.isOther = action.payload
    },
    setCount: (state, action) => {
      state.count = action.payload
    },
    setBiometric: (state, action) => {
      state.biometric = action.payload
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload
    },
    setEmployeeShift: (state, action) => {
      state.employeeShift = action.payload
    },
    setMonthSelection: (state, action) => {
      state.monthSelection = action.payload
    },
    setPaginationCache: (state, action) => {
      state.paginationCache = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployeeAttendanceReport.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.employeeAttendanceReport = action.payload.list
        state.days = action.payload.days
        state.size = action.payload.size
      })
      .addCase(searchAttendance.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.employeeAttendanceReport = action.payload.list
        state.size = action.payload.size
      })
      .addMatcher(
        isAnyOf(getEmployeeAttendanceReport.pending, searchAttendance.pending),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
  },
})

const employeeAttendanceReport = (
  state: RootState,
): EmployeeDetailsWithAttendanceReport[] =>
  state.employeeAttendanceReport.employeeAttendanceReport

const listSize = (state: RootState): number =>
  state.employeeAttendanceReport.size

const isLoading = (state: RootState): LoadingState =>
  state.employeeAttendanceReport.isLoading

const days = (state: RootState): number[] => state.employeeAttendanceReport.days

const monthDisplay = (state: RootState): string =>
  state.employeeAttendanceReport.monthDisplay

const isView = (state: RootState): boolean =>
  state.employeeAttendanceReport.isView

const employeeStatus = (state: RootState): string =>
  state.employeeAttendanceReport.employeeStatus

const date = (state: RootState): Date | null =>
  state.employeeAttendanceReport.date

const isOther = (state: RootState): string =>
  state.employeeAttendanceReport.isOther
const count = (state: RootState): number => state.employeeAttendanceReport.count

const getBiometric = (state: RootState): string =>
  state.employeeAttendanceReport.biometric

const getSearchValue = (state: RootState): string =>
  state.employeeAttendanceReport.searchValue

const getEmployeeShift = (state: RootState): string =>
  state.employeeAttendanceReport.employeeShift

const getMonthSelection = (state: RootState): number =>
  state.employeeAttendanceReport.monthSelection

const getPaginationCache = (
  state: RootState,
): {
  currentPage: number
  pageSize: number
} => state.employeeAttendanceReport.paginationCache

const attendanceReportThunk = {
  getEmployeeAttendanceReport,
  searchAttendance,
}

const attendanceReportSelectors = {
  employeeAttendanceReport,
  listSize,
  isLoading,
  days,
  monthDisplay,
  isView,
  employeeStatus,
  date,
  isOther,
  count,
  getBiometric,
  getSearchValue,
  getEmployeeShift,
  getMonthSelection,
  getPaginationCache,
}

export const attendanceReportService = {
  ...attendanceReportThunk,
  actions: attendanceReportSlice.actions,
  selectors: attendanceReportSelectors,
}

export default attendanceReportSlice.reducer
